import { useState } from "react";
import { Clock } from "lucide-react";

/**
 * Festive artwork slots (Diwali asset pack integration, Phase 1).
 * Every decorative surface is a single image slot: when the pack's real
 * transparent PNG exists at the expected path it renders; until then a
 * faithful hand-drawn version shows. Dropping files into /public/festive/
 * swaps the artwork with zero code or layout changes.
 *
 * Expected files (Phase 2 upload):
 *   /festive/offer-badge.png   — vermilion scalloped "Diwali Offer" badge
 *   /festive/sale-badge.png    — "Limited Time Sale" badge
 *   /festive/divider.png       — saffron diya-and-dots divider strip
 *   /festive/corner.png        — rangoli corner flourish (auto-mirrored)
 */

function FestArt({ src, alt, className = "", fallback, testid }) {
  const [failed, setFailed] = useState(false);
  if (failed) return fallback;
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      draggable="false"
      onError={() => setFailed(true)}
      className={className}
      data-testid={testid}
    />
  );
}

/* ── drawn fallbacks (pack-faithful) ── */

/** Offer badge — the classic PAQVERO festive pill: vermilion rounded badge
 *  with a dashed inner border and ✦ spark accents (restored per owner). */
function DrawnOfferBadge({ text }) {
  return (
    <span className="pq-offer-badge" aria-hidden="true">
      <span className="ob-spark">✦</span> {text} <span className="ob-spark">✦</span>
    </span>
  );
}

/** Limited Time Sale badge — drawn saffron badge with clock, pack-style. */
function DrawnSaleBadge({ text }) {
  return (
    <span className="pq-sale-badge-drawn" aria-hidden="true">
      <span className="sb-clock"><Clock className="h-3 w-3" strokeWidth={2.6} /></span>
      {text}
    </span>
  );
}

/** Diya-and-dots divider — centre diya flanked by dotted strands. */
function DrawnDivider() {
  return (
    <span className="pq-divider-drawn" aria-hidden="true">
      <svg viewBox="0 0 480 34" className="pq-div-svg">
        <g fill="#D97706" opacity="0.9">
          {Array.from({ length: 9 }).map((_, i) => (
            <circle key={`l${i}`} cx={18 + i * 22} cy="17" r={i % 2 ? 1.8 : 2.6} />
          ))}
          {Array.from({ length: 9 }).map((_, i) => (
            <circle key={`r${i}`} cx={282 + i * 22} cy="17" r={i % 2 ? 1.8 : 2.6} />
          ))}
        </g>
        <g transform="translate(226 0)">
          <path d="M14 2c3.4 4.4 4.8 7 4.8 9.6a4.8 4.8 0 0 1-9.6 0C9.2 9 10.6 6.4 14 2z" fill="#F4A51C" />
          <path d="M14 7.6c1.4 1.8 2 3.1 2 4.3a2 2 0 0 1-4 0c0-1.2.6-2.5 2-4.3z" fill="#FFF4C2" />
          <path d="M1 17c4.3 1.8 8.6 2.5 13 2.5s8.7-.7 13-2.5c-.5 7.1-5.6 10.5-13 10.5S1.5 24.1 1 17z" fill="#C83E24" />
          <ellipse cx="14" cy="17.2" rx="13" ry="2.7" fill="#D97706" />
        </g>
      </svg>
    </span>
  );
}

/** Rangoli corner flourish — curved paisley strands + dots. */
function DrawnCorner() {
  return (
    <svg viewBox="0 0 74 74" fill="none" aria-hidden="true" style={{ width: "100%", height: "100%" }}>
      <path d="M4 70V24C4 13 13 4 24 4h46" stroke="#F4A51C" strokeWidth="2.2" strokeLinecap="round" />
      <path d="M13 70V31c0-9.5 7.5-17 17-17h40" stroke="#C83E24" strokeWidth="1.8" strokeLinecap="round" strokeDasharray="1 6" />
      <path d="M22 70V38c0-8.5 7-15.5 15.5-15.5H70" stroke="#FFD88A" strokeWidth="1.4" strokeLinecap="round" strokeDasharray="1 5" />
      <circle cx="9" cy="62" r="3.4" fill="#C83E24" />
      <circle cx="62" cy="9" r="3.4" fill="#C83E24" />
      <circle cx="18" cy="52" r="2.2" fill="#D97706" />
      <circle cx="52" cy="18" r="2.2" fill="#D97706" />
      <circle cx="27" cy="43" r="1.6" fill="#F4A51C" />
      <circle cx="43" cy="27" r="1.6" fill="#F4A51C" />
    </svg>
  );
}

/* ── public slots ── */

export function DiwaliOfferBadge({ text = "Diwali Offer", testid = "diwali-offer-badge", className = "" }) {
  return (
    <span className={`pq-offer-slot ${className}`} data-testid={testid}>
      <FestArt
        src="/festive/offer-badge.png"
        alt={text}
        testid={`${testid}-img`}
        className="pq-offer-slot-img"
        fallback={<DrawnOfferBadge text={text} />}
      />
    </span>
  );
}

export function LimitedTimePill({ text = "Limited Time Sale", testid = "limited-time-pill", className = "" }) {
  return (
    <span className={`pq-sale-slot ${className}`} data-testid={testid}>
      <FestArt
        src="/festive/sale-badge.png"
        alt={text}
        testid={`${testid}-img`}
        className="pq-sale-slot-img"
        fallback={<DrawnSaleBadge text={text} />}
      />
    </span>
  );
}

export function FestiveDivider({ testid = "festive-divider", className = "" }) {
  return (
    <span className={`pq-divider-slot ${className}`} data-testid={testid}>
      <FestArt
        src="/festive/divider.png"
        alt=""
        testid={`${testid}-img`}
        className="pq-divider-slot-img"
        fallback={<DrawnDivider />}
      />
    </span>
  );
}

/** Corner pair for festive promo panels (top-left + mirrored bottom-right). */
export function RangoliCorners() {
  const corner = (
    <FestArt
      src="/festive/corner.png"
      alt=""
      testid="rangoli-corner-img"
      className="pq-corner-slot-img"
      fallback={<DrawnCorner />}
    />
  );
  return (
    <>
      <span className="pq-rangoli-corner rc-tl">{corner}</span>
      <span className="pq-rangoli-corner rc-br">{corner}</span>
    </>
  );
}
